// Shared formatting helpers for the page components.
//
// **Why a local module rather than a shared `@sb-ai-platform/console-shared`
// package**: the console is an app, not a library (see `index.ts`
// doc-comment). We keep helpers local to the package; if a future task
// wants these in the SDK we lift them THEN.

/**
 * Format an ISO-8601 timestamp as a short local time string. Used in
 * approval cards + run list rows. We deliberately avoid `Intl.RelativeTimeFormat`
 * + 1-second tickers — the user gets a fixed string, the page can
 * be re-rendered on focus to refresh.
 */
export function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * Short HH:mm:ss.mmm time-of-day — used for per-event timestamps on
 * the transcript so the operator can correlate against logs without
 * needing the full date.
 */
export function formatClockTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.toLocaleTimeString(undefined, { hour12: false })}.${String(d.getMilliseconds()).padStart(3, '0')}`;
  } catch {
    return '';
  }
}

/**
 * Delta between two ISO timestamps as a compact "+1.2s" / "+250ms"
 * string. Returns the empty string for missing/invalid inputs.
 */
export function formatDelta(fromIso: string | undefined, toIso: string | undefined): string {
  if (!fromIso || !toIso) return '';
  try {
    const a = new Date(fromIso).getTime();
    const b = new Date(toIso).getTime();
    if (!Number.isFinite(a) || !Number.isFinite(b)) return '';
    const ms = b - a;
    if (ms < 0) return '';
    if (ms < 1000) return `+${ms}ms`;
    if (ms < 60_000) return `+${(ms / 1000).toFixed(1)}s`;
    return `+${Math.round(ms / 1000)}s`;
  } catch {
    return '';
  }
}

/**
 * Tool-name truncation for display in lists. Tool names from MCP
 * servers can be long; the list view shows the first 40 chars with
 * an ellipsis if more. The full name is in `title=` on the rendered
 * span.
 */
export function truncateToolName(name: string, max = 40): string {
  if (name.length <= max) return name;
  return `${name.slice(0, max - 1)}…`;
}

/**
 * Map a session status to a user-facing string + a CSS class suffix.
 * Avoids hard-coding the enum in TSX — UI tests can drive the rendered
 * status text via a stable key.
 */
export function sessionStatusLabel(status: string): string {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'running':
      return 'Running';
    case 'awaiting_user':
      return 'Awaiting user';
    case 'awaiting_approval':
      return 'Awaiting approval';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    case 'cancelled':
      return 'Cancelled';
    case 'expired':
      return 'Expired';
    default:
      return status;
  }
}
